import { BenefitAnalysis, FareCharge, FareFamilyFareRule, FareFamilySolution, FareRuleData, Plan, TooltipItem, TooltipLabel, TransformationContext, TransformFareFamilyProps } from "@/src/components/desktop/srp/fareFamily/types"
import { FlightCard } from "@/src/types/flight"
import { getBrandName } from "@/src/utils/buildUtils"
import { replacePlaceHolderString } from "../../generalUtils"
import { CouponDetails } from "../flightCardProps"

/**
 * Flag for the airline-charges display flow (single source of truth).
 * false -> current flow (unchanged). true -> cancel/amend "from" values use
 * charges[0].airlineCharge, cancel row heading becomes "Airline cancellation fees",
 * and tooltips get a "{brand} charges will be applicable" note.
 * TODO: wire this to AB config when available.
 */
export const SHOW_AIRLINE_CHARGES = true;

const fareFamilyBadgeConfig : Record<string, Record<string, string>> = {
  F3 : {
    FlyPlus : 'Most Preferred',
  },
  XY : {
    VALUE : 'Most Preferred',
  },
  "6E" : {
    SAVER : 'Most Preferred',
  },
  'G9' : {
    VALUE : 'Most Preferred',
  },
  'E5' : {
    VALUE : 'Most Preferred',
  },
  '9P' : {
    VALUE : 'Most Preferred',
  },
  '3L' : {
    VALUE : 'Most Preferred',
  },
  "IX" : {
    "XPRESS VALUE" : 'Most Preferred',
  }
}

/**
 * Transforms raw fare family solutions into a structured format for UI consumption
 * @param solutions Raw fare family data from API
 * @param formatCurrency Currency formatting function
 * @param formatDate Date formatting function
 * @param translations Translation function
 * @returns Structured plans data
 */
// Updated function using destructuring with the new interface
export const transformFareFamily = ({
  solutions,
  formatCurrency,
  formatDate,
  translations,
  flight,
  isPerPaxPricingEnabled,
}: TransformFareFamilyProps): { plans: Plan[][] } => {
  if (!solutions?.length) {
    return { plans: [] };
  }

  const context: TransformationContext = {
    formatCurrency,
    formatDate,
    translations,
    flight,
    isPerPaxPricingEnabled,
  };

  const plans: Plan[][] = solutions.map((solution) =>
    transformSolution(solution, context)
  );

  return { plans };
};


/**
 * Transforms a single solution into an array of plans
 */
function transformSolution(solution: FareFamilySolution, context: TransformationContext): Plan[] {
  const { fareFamilyFareRules = [] } = solution;
  return fareFamilyFareRules.map(fareRule => transformFareRule(fareRule, context));
}

/**
 * Transforms a single fare rule into a plan
 */
function transformFareRule(fareRule: FareFamilyFareRule, context: TransformationContext): Plan {
  const {
    fareGroupDisplayName,
    totalPrice,
    baggage = [],
    cancel,
    amend,
    seat,
    meal,
    comboFbc,
    fareKey,
    basePrice,
    solutionId,
    ctBrandName,
    perPaxPricing: { adultPrice } = {},
    unModifiedTotalPrice,
    planPricePrefix,
    showTotalRTPriceMessage,
    bookingClass

  } = fareRule
  const { formatCurrency,isPerPaxPricingEnabled } = context;
  const rawCouponDetails = context.flight?.priceBreakup?.fare?.coupon_detail;
  const couponDetail = rawCouponDetails?.discountAmount
    ? {
        ...rawCouponDetails,
        discountedPrice: formatCurrency(totalPrice - rawCouponDetails.discountAmount),
        discountAmount: formatCurrency(rawCouponDetails.discountAmount),
      }
    : {};

    // For Per Pax Pricing
  const effectivePrice = formatCurrency(isPerPaxPricingEnabled && adultPrice ? adultPrice : totalPrice)

  // Analyze benefits and amenities
  const benefitAnalysis = analyzeBenefits({ cancel, amend, seat, meal }, baggage);

  // Create baggage text mapping
  const baggageText = createBaggageTextMap(baggage);

  // Generate benefits text for display
  const benefitsText = createBenefitsText(
    benefitAnalysis.included,
    { cancel, amend, seat, meal, couponDetail },
    baggageText,
    context
  );

  // Create tooltip data for drawer
  const toolTipData = createTooltipData(
    { cancel, amend },
    baggage,
    fareGroupDisplayName,
    effectivePrice,
    context
  );

  return {
    title: fareGroupDisplayName,
    price: totalPrice,
    displayPrice: effectivePrice,
    badge: isMostPreferedFare(context.flight, fareGroupDisplayName),
    included: benefitAnalysis.included,
    originallyIncluded: { ...benefitAnalysis.included },
    benefitsText,
    freeAmenities: benefitAnalysis.freeAmenities,
    fareKey,
    comboFbc,
    basePrice,
    solutionId,
    toolTipData,
    isPerPaxPricingDefined : !!(isPerPaxPricingEnabled && adultPrice),
    ctBrandName,
    unModifiedTotalPrice,
    planPricePrefix,
    showTotalRTPriceMessage,
    bookingClass
  };
}

/**
 * Analyzes fare rule benefits and amenities
 */
function analyzeBenefits(fareRuleData: FareRuleData, baggage: any[]): BenefitAnalysis {
  const { cancel, amend, seat, meal } = fareRuleData;

  const included = {
    cabinBaggage: baggage?.some(bag => bag.baggageType === 'CARRY_ON' && bag?.permitted) || false,
    checkinBaggage: baggage?.some(bag => bag.baggageType === 'CHECK_IN' && bag?.permitted) || false,
    cancel: cancel?.permitted ?? false,
    amend: amend?.permitted ?? false,
    seat: !seat?.chargeable,
    meal: !meal?.chargeable,
  };

  const freeAmenities = {
    checkinBaggage: included.checkinBaggage,
    seat: included.seat,
    meal: included.meal,
  };

  return { included, freeAmenities, benefitsText: {} };
}

/**
 * Creates a mapping of baggage types to display text
 */
function createBaggageTextMap(baggage: any[]): Record<string, string> {
  if (!baggage?.length) return {};

  return baggage.reduce<Record<string, string>>((acc, bag) => {
    const displayText = bag.baggageTypeDisplayText || '';
    const description = bag.baggageDescription || '';
    acc[bag.baggageType] = `${displayText} ${description}`.trim();
    return acc;
  }, {});
}

/**
 * Creates benefits text for display
 */
function createBenefitsText(
  included: Record<string, boolean>,
  fareRuleData: FareRuleData,
  baggageText: Record<string, string>,
  context : TransformationContext,
): Plan['benefitsText'] {
  const { cancel, amend, seat, meal, couponDetail } = fareRuleData;
  const {formatCurrency , isPerPaxPricingEnabled, translations} = context
  return {
    checkinBaggage: baggageText['CHECK_IN'] || '',
    cabinBaggage: baggageText['CARRY_ON'] || '',
    cancel: buildChargeText(cancel, included.cancel, formatCurrency, 'cancel', translations),
    amend: buildChargeText(amend, included.amend, formatCurrency, 'amend', translations),
    seat: seat?.description || '',
    meal: meal?.description || '',
    exclusiveBenefits: replacePlaceHolderString(fetchAppropriateMessage(isPerPaxPricingEnabled, couponDetail), couponDetail) || null
  };
}

/**
 * Builds charge text for cancel/amend benefits
 */
function buildChargeText(
  ruleData: any,
  isIncluded: boolean,
  formatCurrency: (price: number, usePrecision?: boolean) => string,
  type: TooltipLabel,
  translations: (key: string, values?: Record<string, any>) => string,
): string {
  if (!ruleData?.fairDisplayInfoText) return '';

  const baseText = ruleData.fairDisplayInfoText;
  if (!isIncluded) {
    return baseText;
  }

  if (SHOW_AIRLINE_CHARGES) {
    const airlineChargeAmount = ruleData?.charges?.[0]?.airlineCharge?.totalCharge;
    if (airlineChargeAmount !== undefined && airlineChargeAmount !== null) {
      // Cancel: replace text and value; Amend: keep text, replace only the value
      const displayText = type === 'cancel'
        ? translations('fareFamily.airlineCancellationChargesFrom')
        : baseText;
      return `${displayText} ${formatCurrency(airlineChargeAmount)}`;
    }
  }

  return `${baseText} ${formatCurrency(ruleData.totalRefundableAmount ? ruleData.totalRefundableAmount : ruleData.totalCharge)}`;
}

/**
 * Creates tooltip data for the fare rules drawer
 */
function createTooltipData(
  fareRules: { cancel?: any; amend?: any },
  baggage: any[],
  fareTitle: string,
  price: string,
  context: TransformationContext
) {
  const { translations } = context;

  const brandChargesNote = SHOW_AIRLINE_CHARGES
    ? translations('fareFamily.brandChargesApplicable', { brand: getDisplayBrandName() })
    : undefined;

  return {
    cancel: {
      // When the airline-charges flow is enabled, hide the refund-on-cancellation
      // rows in the tooltip and show only the brand charges note
      heading: SHOW_AIRLINE_CHARGES ? '' : translations('fareFamily.refundAmountOnCancellation'),
      data: SHOW_AIRLINE_CHARGES
        ? []
        : getToolTipData(fareRules.cancel?.charges, 'cancel', context.formatDate, context.formatCurrency, context.translations),
      note: brandChargesNote
    },
    amend: {
      heading: translations('fareFamily.dateChangeFee'),
      data: getToolTipData(fareRules.amend?.charges, 'amend', context.formatDate, context.formatCurrency, context.translations),
      note: brandChargesNote
    },
    processedFareRules: createProcessedFareRules(fareRules, context),
    baggageData: [], // Using rawBaggage instead
    rawBaggage: baggage,
    fareTitle,
    price
  };
}

/**
 * Creates tooltip data for fare charges
 */
export function getToolTipData(
  charges: FareCharge[] = [],
  key: TooltipLabel,
  formatDate: (date: string, format?: string) => string,
  formatCurrency: (price: number, usePrecision?: boolean) => string,
  translations: (key: string) => string
): TooltipItem[] {
  if (!charges?.length) return [];

  return charges.map((charge, index) => {
    const startTime = index === 0
      ? translations('common.now')
      : formatDate(new Date(Number(charge.timeline.startTime)).toISOString(), 'DD MMM, HH:mm');

    const endTime = formatDate(
      new Date(Number(charge.timeline.endTime)).toISOString(),
      'DD MMM, HH:mm'
    );

    const amount = key === 'cancel' ? charge?.amount?.amountRefundable : getAmendTooltipAmount(charge);
    const totalCharge = charge.permitted
      ? formatCurrency(amount)
      : key === 'cancel'
        ? translations('common.nonRefundable')
        : translations('common.nonChangeable');

    return {
      time: `${startTime} - ${endTime}`,
      charge: totalCharge
    };
  });
}

/**
 * Amend tooltip row amount. Airline-charges flow: total charge minus the flyin/brand charge
 */
function getAmendTooltipAmount(charge: FareCharge): number {
  const totalCharge = charge?.amount?.totalCharge;
  if (SHOW_AIRLINE_CHARGES && totalCharge != null) {
    return Math.max(totalCharge - (charge?.flyinCharge?.totalCharge ?? 0), 0);
  }
  return totalCharge;
}

/**
 * Creates processed fare rules for the drawer
 */
function createProcessedFareRules(
  fareRules: { cancel?: any; amend?: any },
  context: TransformationContext
) {
  const { formatDate, formatCurrency, translations } = context;
  const processedRules = [];

  // Process cancellation rules
  if (fareRules.cancel?.charges?.length > 0) {
    const tooltipData = getToolTipData(
      fareRules.cancel.charges,
      'cancel',
      formatDate,
      formatCurrency,
      translations
    );

    processedRules.push(createFareRuleItem(
      'CANCEL',
      fareRules.cancel,
      tooltipData,
      'Cancellation Policy'
    ));
  }

  // Process amendment rules
  if (fareRules.amend?.charges?.length > 0) {
    const tooltipData = getToolTipData(
      fareRules.amend.charges,
      'amend',
      formatDate,
      formatCurrency,
      translations
    );

    processedRules.push(createFareRuleItem(
      'AMEND',
      fareRules.amend,
      tooltipData,
      'Date Change Policy'
    ));
  }

  return processedRules;
}

/**
 * Creates a single fare rule item
 */
function createFareRuleItem(
  type: 'CANCEL' | 'AMEND',
  ruleData: any,
  tooltipData: TooltipItem[],
  title: string
) {
  return {
    type,
    charges: ruleData.charges,
    permitted: ruleData.permitted,
    timeSegments: tooltipData.map(item => item.time),
    priceSegments: tooltipData.map(item => ({
      priceValue: item.charge,
      priceBreakUp: []
    })),
    segmentCount: ruleData.charges.length,
    title
  };
}

function isMostPreferedFare(flight: FlightCard, fareGroupDisplayName: string): string | null {
  const airlineCode = flight?.airlineCodes?.[0];
  const badgeText = fareFamilyBadgeConfig?.[airlineCode]?.[fareGroupDisplayName];
  return badgeText || null;
}

function fetchAppropriateMessage(isPerPaxPricingEnabled : boolean, coupon_detail : CouponDetails) {
  return isPerPaxPricingEnabled ? coupon_detail.adultCouponMessage : coupon_detail.message
}

/**
 * Returns the OTA brand name (Flyin/Cleartrip) with first letter capitalized for display
 */
export function getDisplayBrandName(): string {
  const brand = getBrandName();
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

